import React, { useCallback } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { HiringCopilotModelByJobTitleConfig } from 'libs/end-to-end/src/types/databases/scheduling/hiring-copilot-model-by-job-title-config';
import { HiringCopilotJobTitleModelEntry } from 'libs/end-to-end/src/types/databases/scheduling/hiring-copilot-model-by-job-title-config';
import { Autocomplete } from '@mui/material';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const DEFAULT_ALIAS = 'prod';

const newEntry = (): HiringCopilotJobTitleModelEntry => ({
  jobTitleOrJobTitleGroup: '',
  alias: DEFAULT_ALIAS,
});

export interface ConfigureHiringCopilotModelByJobTitleProps {
  jobTitleSuggestions: string[];
  details: HiringCopilotModelByJobTitleConfig;
  updateDetails: (config: HiringCopilotModelByJobTitleConfig) => void;
  /** When true, display "job title group" in labels/copy instead of "job title". */
  useJobTitleGroups: boolean;
}

const CARD_TITLE_JOB_TITLE = 'Hiring Copilot: model by job title';
const CARD_TITLE_JOB_TITLE_GROUP = 'Hiring Copilot: model by job title group';
const TOGGLE_LABEL_JOB_TITLE = 'Enable model selection by job title';
const TOGGLE_LABEL_JOB_TITLE_GROUP =
  'Enable model selection by job title group';
const EMPTY_STATE_JOB_TITLE =
  'No entries. Add a row to assign an alias per job title.';
const EMPTY_STATE_JOB_TITLE_GROUP =
  'No entries. Add a row to assign an alias per job title group.';
const LABEL_JOB_TITLE = 'Job title';
const LABEL_JOB_TITLE_GROUP = 'Job title group';

export const ConfigureHiringCopilotModelByJobTitle = ({
  jobTitleSuggestions,
  details,
  updateDetails,
  useJobTitleGroups,
}: ConfigureHiringCopilotModelByJobTitleProps) => {
  const cardTitle = useJobTitleGroups
    ? CARD_TITLE_JOB_TITLE_GROUP
    : CARD_TITLE_JOB_TITLE;
  const toggleLabel = useJobTitleGroups
    ? TOGGLE_LABEL_JOB_TITLE_GROUP
    : TOGGLE_LABEL_JOB_TITLE;
  const emptyStateText = useJobTitleGroups
    ? EMPTY_STATE_JOB_TITLE_GROUP
    : EMPTY_STATE_JOB_TITLE;
  const rowLabel = useJobTitleGroups ? LABEL_JOB_TITLE_GROUP : LABEL_JOB_TITLE;

  const isEnabled = details?.usePretrainedModel ?? false;
  const toggleEnabled = useCallback(() => {
    if (isEnabled) {
      updateDetails({
        usePretrainedModel: false,
        useJobTitleGroups,
        modelByJobTitle: details.modelByJobTitle,
      });
    } else {
      updateDetails({
        usePretrainedModel: true,
        useJobTitleGroups,
        modelByJobTitle: details.modelByJobTitle,
      });
    }
  }, [isEnabled, updateDetails, details.modelByJobTitle, useJobTitleGroups]);

  const items = details?.modelByJobTitle ?? [];
  const entriesSummary =
    items.length === 0
      ? 'No entries'
      : `${items.length} ${items.length === 1 ? 'entry' : 'entries'}`;

  const setItems = useCallback(
    (newItems: HiringCopilotJobTitleModelEntry[]) => {
      updateDetails({
        usePretrainedModel: isEnabled,
        useJobTitleGroups,
        modelByJobTitle: newItems,
      });
    },
    [updateDetails, isEnabled, useJobTitleGroups],
  );

  const crud = useCrudOperations({
    items,
    setItems,
  });

  const handleAdd = useCallback(() => {
    crud.onAdd(newEntry());
  }, [crud]);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h3 className="text-lg font-semibold">{cardTitle}</h3>
      <S2Checkbox checked={isEnabled} onChange={toggleEnabled}>
        {toggleLabel}
      </S2Checkbox>
      {isEnabled && (
        <Accordion defaultExpanded elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
            <span className="text-gray-700">Configure ({entriesSummary})</span>
          </AccordionSummary>
          <AccordionDetails className="pt-0">
            <Spacing size={'md'} direction={'vertical'}>
              <div className="flex justify-between items-center">
                <Button
                  behavior={{ type: 'onClick', onClick: handleAdd }}
                  text="+ Add row"
                  size="small"
                  variant="primary"
                />
              </div>
              {items.length === 0 && (
                <div className="text-gray-500 italic">{emptyStateText}</div>
              )}
              {items.map((entry, index) => (
                <Box
                  key={index}
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  alignItems="center"
                  gap="xs"
                >
                  <Autocomplete
                    freeSolo
                    options={jobTitleSuggestions}
                    value={entry.jobTitleOrJobTitleGroup}
                    onChange={(_event, value) => {
                      crud.onEdit(index, {
                        jobTitleOrJobTitleGroup:
                          typeof value === 'string' ? value : (value ?? ''),
                      });
                    }}
                    onInputChange={(_event, value) => {
                      crud.onEdit(index, { jobTitleOrJobTitleGroup: value });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={rowLabel}
                        size="small"
                        sx={{ minWidth: 200 }}
                      />
                    )}
                  />
                  <TextField
                    label="Alias"
                    size="small"
                    value={entry.alias ?? DEFAULT_ALIAS}
                    onChange={(e) =>
                      crud.onEdit(index, {
                        alias: e.target.value || DEFAULT_ALIAS,
                      })
                    }
                    sx={{ minWidth: 100 }}
                  />
                  <IconButton
                    aria-label="Remove row"
                    onClick={() => crud.onDelete(index)}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Spacing>
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};
